// src/app/components/ValidationDecisionStatusSection.tsx

import {
  IRoomValidationStatusCode,
  IRoomValidationDecisions,
  IRoomValidationStatusEntry,
  IRoomValidationStatusState,
} from "@backend/src/shared/schemas/schemas";
import {
  Stack,
  Badge,
  Tooltip,
  ActionIcon,
  Divider,
  Select,
  Textarea,
  Button,
  Collapse,
  Group,
  Text,
  Box,
} from "@mantine/core";
import { useState, useMemo, useEffect } from "react";
import { FaHistory } from "react-icons/fa";
import { FaClockRotateLeft } from "react-icons/fa6";
import { useAppSelector } from "../../app/hooks";
import {
  ROOM_VALIDATION_STATUS_CODES,
  ROOM_VALIDATION_STATUS_LABELS,
} from "../../constantsFromBackend";
import { ROOM_VALIDATION_STATUS_COLORS } from "../../RoomColoring";
import { usePostMultiplacerRoomValidationDecisionStatusMutation } from "../../../openapi";
import { selectHttpSingleRoomFull } from "../../Room/httpRoomSelectors";

type StatusSectionProps = {
  /**
   * Key på formen "<validationKey>.<discipline>"
   * f.eks. "standardRoom.E", "standardRoom.V", "rie.RIV" osv.
   */
  validationDecisionKey: string;
};

const ValidationDecisionStatusSection: React.FC<StatusSectionProps> = ({
  validationDecisionKey,
}) => {
  const projectNo = useAppSelector((s) => s.design.projectNo);
  const room = useAppSelector(selectHttpSingleRoomFull);

  const [selectedStatus, setSelectedStatus] = useState<IRoomValidationStatusCode | null>(null);
  const [comment, setComment] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const [setStatusMutation, { isLoading: isSaving }] =
    usePostMultiplacerRoomValidationDecisionStatusMutation();

  // Splitta key: "standardRoom.E" -> ["standardRoom", "E"]
  const [validationKey, discipline] = useMemo(() => {
    const parts = validationDecisionKey.split(".");
    return [parts[0], parts[1]] as [string | undefined, string | undefined];
  }, [validationDecisionKey]);

  const statusStateForDiscipline = useMemo<IRoomValidationStatusState>(() => {
    if (!room || !validationKey || !discipline) {
      return { current: null, history: [], comments: [] };
    }

    const decisions: IRoomValidationDecisions | undefined = room.validationDecisions;
    if (!decisions) {
      return { current: null, history: [], comments: [] };
    }

    // validationDecisions[validationKey] er en record<string, IRoomValidationStatusState>
    const record = (decisions as any)[validationKey] as
      | Record<string, IRoomValidationStatusState>
      | undefined;

    if (!record) {
      return { current: null, history: [], comments: [] };
    }

    return record[discipline] ?? { current: null, history: [], comments: [] };
  }, [room, validationKey, discipline]);

  const currentEntry: IRoomValidationStatusEntry | undefined | null =
    statusStateForDiscipline.current ?? null;

  const historyForDiscipline: IRoomValidationStatusEntry[] = useMemo(() => {
    const list = statusStateForDiscipline.history ?? [];
    // Nyeste først
    return [...list].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [statusStateForDiscipline.history]);

  const statusOptions = useMemo(
    () =>
      ROOM_VALIDATION_STATUS_CODES.map((code) => ({
        value: code,
        label: ROOM_VALIDATION_STATUS_LABELS[code],
      })),
    []
  );

  // Default til gjeldende status når vi har en entry og ingen valgt ennå
  useEffect(() => {
    if (currentEntry && !selectedStatus) {
      setSelectedStatus(currentEntry.status);
    }
  }, [currentEntry, selectedStatus]);

  const handleSubmit = async () => {
    if (!room || !projectNo || !selectedStatus || !validationKey || !discipline) {
      return;
    }

    const trimmedComment = comment.trim();
    const commentToSend = trimmedComment.length > 0 ? trimmedComment : undefined;

    try {
      await setStatusMutation({
        body: {
          projectNo,
          roomNumber: room.roomNumber,
          decisionKey: validationDecisionKey,
          status: selectedStatus,
          comment: commentToSend,
        },
      }).unwrap();

      // Tøm kommentar, behold valgt status
      setComment("");
    } catch (error) {
      console.error("Failed to update validation decision status", error);
      // Ev. Mantine-notification her
    }
  };

  const latestHistoryEntry = historyForDiscipline[0];

  const disciplineLabel = discipline ?? "";

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center">
        <Text fw={600} size="sm">
          Valideringsstatus – {disciplineLabel}
        </Text>

        <Group gap="xs">
          {currentEntry && (
            <Badge
              variant="light"
              size="sm"
              style={{
                backgroundColor: ROOM_VALIDATION_STATUS_COLORS[currentEntry.status],
                color: "#ffffff",
              }}
            >
              {ROOM_VALIDATION_STATUS_LABELS[currentEntry.status]}
            </Badge>
          )}
          <Tooltip label={showHistory ? "Skjul historikk" : "Vis historikk"} position="left">
            <ActionIcon variant="subtle" size="sm" onClick={() => setShowHistory((prev) => !prev)}>
              <FaHistory size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* Siste beslutning */}
      {latestHistoryEntry ? (
        <Box
          p="xs"
          style={{
            borderRadius: 6,
            backgroundColor: "#edf2ff",
          }}
        >
          <Group justify="space-between" align="flex-start" gap="xs">
            <Stack gap={4} style={{ flex: 1 }}>
              <Text size="xs" fw={500}>
                Siste beslutning
              </Text>
              <Group gap="xs" align="center">
                <Badge
                  size="xs"
                  style={{
                    backgroundColor: ROOM_VALIDATION_STATUS_COLORS[latestHistoryEntry.status],
                    color: "#ffffff",
                  }}
                >
                  {ROOM_VALIDATION_STATUS_LABELS[latestHistoryEntry.status]}
                </Badge>
                {latestHistoryEntry.comment && <Text size="xs">{latestHistoryEntry.comment}</Text>}
              </Group>
              <Text size="xs" c="dimmed">
                {latestHistoryEntry.updatedByUserName} ·{" "}
                {new Date(latestHistoryEntry.updatedAt).toLocaleString("nb-NO")}
              </Text>
            </Stack>
            <Tooltip label="Historikken er sortert med nyeste først" position="left">
              <Box style={{ marginTop: 4 }}>
                <FaClockRotateLeft size={14} />
              </Box>
            </Tooltip>
          </Group>
        </Box>
      ) : (
        <Text size="xs" c="dimmed">
          Ingen beslutninger registrert ennå for denne disiplinen.
        </Text>
      )}

      <Divider my="xs" />

      {/* Skjema for å oppdatere status */}
      <Stack gap="xs">
        <Select
          label="Status"
          placeholder="Velg status"
          data={statusOptions}
          value={selectedStatus ?? null}
          onChange={(value) =>
            setSelectedStatus(value ? (value as IRoomValidationStatusCode) : null)
          }
          disabled={isSaving}
          size="xs"
        />

        <Textarea
          label="Kommentar (valgfritt)"
          placeholder="Legg til en kort forklaring…"
          minRows={2}
          maxRows={4}
          autosize
          value={comment}
          onChange={(event) => setComment(event.currentTarget.value)}
          disabled={isSaving}
          size="xs"
        />

        <Group justify="flex-end">
          <Button
            size="xs"
            onClick={handleSubmit}
            loading={isSaving}
            disabled={!room || !selectedStatus || !validationKey || !discipline}
          >
            Lagre status
          </Button>
        </Group>
      </Stack>

      {/* Historikkseksjon (skjules/vises via knapp) */}
      <Collapse in={showHistory}>
        <Stack gap="xs" mt="xs">
          <Text size="xs" fw={500}>
            Historikk
          </Text>
          {historyForDiscipline.length === 0 ? (
            <Text size="xs" c="dimmed">
              Ingen historikk ennå for denne disiplinen.
            </Text>
          ) : (
            historyForDiscipline.map((entry, index) => (
              <Box
                key={`${entry.updatedByUserId}-${entry.updatedAt}-${index}`}
                p="xs"
                style={{
                  borderRadius: 6,
                  border: "1px dashed #e0e0e0",
                  backgroundColor: "#ffffff",
                }}
              >
                <Stack gap={2}>
                  <Badge
                    size="xs"
                    style={{
                      backgroundColor: ROOM_VALIDATION_STATUS_COLORS[entry.status],
                      color: "#ffffff",
                      alignSelf: "flex-start",
                    }}
                  >
                    {ROOM_VALIDATION_STATUS_LABELS[entry.status]}
                  </Badge>
                  {entry.comment && <Text size="xs">{entry.comment}</Text>}
                  <Text size="xs" c="dimmed">
                    {entry.updatedByUserName} · {new Date(entry.updatedAt).toLocaleString("nb-NO")}
                  </Text>
                </Stack>
              </Box>
            ))
          )}
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default ValidationDecisionStatusSection;
